import { Controller } from "@hotwired/stimulus"

// Dictation Controller
// Handles voice-to-text transcription for AI interview inputs
//
// Usage:
// <div data-controller="dictation"
//      data-dictation-url-value="/path/to/transcribe"
//      data-dictation-target-id-value="message-input">
//   <button data-action="click->dictation#toggle" data-dictation-target="button">
//     <svg data-dictation-target="micIcon">...</svg>
//     <svg data-dictation-target="recordingIcon" class="hidden">...</svg>
//   </button>
//   <div data-dictation-target="status" class="hidden">Recording...</div>
// </div>
//
// Connects to data-controller="dictation"
export default class extends Controller {
  static targets = ["button", "micIcon", "recordingIcon", "status"]

  static values = {
    url: String,                           // Transcription endpoint URL
    targetId: String,                      // ID of the textarea to insert text into
    submitButtonId: String,                // ID of the submit button to disable while recording
    maxDuration: { type: Number, default: 120000 },  // Max recording duration (2 min default)
    enabled: { type: Boolean, default: true }        // Whether dictation is enabled
  }

  connect() {
    this.isRecording = false
    this.mediaRecorder = null
    this.audioChunks = []
    this.recordingTimeout = null
    this.stream = null

    // Check browser support
    this.checkBrowserSupport()
  }

  disconnect() {
    this.cleanup()
  }

  checkBrowserSupport() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.disableButton("Browser doesn't support audio recording")
      return false
    }

    if (!this.enabledValue) {
      this.disableButton("Dictation is disabled")
      return false
    }

    return true
  }

  disableButton(reason) {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true
      this.buttonTarget.title = reason
      this.buttonTarget.classList.add('opacity-50', 'cursor-not-allowed')
    }
  }

  // Toggle recording state
  toggle(event) {
    event.preventDefault()

    if (!this.enabledValue) {
      this.showError("Dictation is currently disabled")
      return
    }

    if (this.isRecording) {
      this.stopRecording()
    } else {
      this.startRecording()
    }
  }

  async startRecording() {
    try {
      // Request microphone access
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      // Determine best supported audio format
      const mimeType = this.getBestMimeType()

      const options = mimeType ? { mimeType } : {}
      this.mediaRecorder = new MediaRecorder(this.stream, options)

      this.audioChunks = []

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data)
        }
      }

      this.mediaRecorder.onstop = async () => {
        const mimeTypeUsed = this.mediaRecorder.mimeType || 'audio/webm'
        const audioBlob = new Blob(this.audioChunks, { type: mimeTypeUsed })
        this.audioChunks = []

        // Release microphone
        this.releaseStream()

        // Send for transcription
        await this.transcribeAudio(audioBlob)
      }

      this.mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error)
        this.stopRecording()
        this.showError('Recording error occurred. Please try again.')
      }

      // Start recording
      this.mediaRecorder.start()
      this.isRecording = true

      // Set max duration timeout
      this.recordingTimeout = setTimeout(() => {
        if (this.isRecording) {
          this.stopRecording()
        }
      }, this.maxDurationValue)

      // Update UI
      this.showRecordingState()

    } catch (error) {
      console.error('Error accessing microphone:', error)
      this.handleMicrophoneError(error)
    }
  }

  stopRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop()
    }

    this.isRecording = false

    // Clear timeout
    if (this.recordingTimeout) {
      clearTimeout(this.recordingTimeout)
      this.recordingTimeout = null
    }

    // Update UI to transcribing state
    this.showTranscribingState()
  }

  async transcribeAudio(audioBlob) {
    try {
      const formData = new FormData()
      formData.append('audio', audioBlob, 'recording.webm')

      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        },
        body: formData,
        credentials: 'same-origin'
      })

      const result = await response.json()

      if (result.success && result.text) {
        this.insertText(result.text)
        this.showIdleState()
      } else {
        throw new Error(result.error || 'Transcription failed')
      }

    } catch (error) {
      console.error('Transcription error:', error)
      this.showError(error.message || 'Failed to transcribe audio. Please try again or type your message.')
      this.showIdleState()
    }
  }

  insertText(text) {
    const targetElement = document.getElementById(this.targetIdValue)

    if (targetElement) {
      // Append to existing text with space if needed
      const currentValue = targetElement.value
      if (currentValue && !currentValue.endsWith(' ')) {
        targetElement.value = currentValue + ' ' + text
      } else {
        targetElement.value = currentValue + text
      }

      // Trigger input event for any listeners
      targetElement.dispatchEvent(new Event('input', { bubbles: true }))

      // Focus and place cursor at end
      targetElement.focus()
      targetElement.setSelectionRange(targetElement.value.length, targetElement.value.length)
    }
  }

  // UI State Management

  showRecordingState() {
    if (this.hasMicIconTarget) {
      this.micIconTarget.classList.add('hidden')
    }
    if (this.hasRecordingIconTarget) {
      this.recordingIconTarget.classList.remove('hidden')
    }
    if (this.hasStatusTarget) {
      this.statusTarget.classList.remove('hidden')
      this.statusTarget.textContent = this.getTranslation('recording')
    }
    if (this.hasButtonTarget) {
      this.buttonTarget.classList.add('text-red-600')
      this.buttonTarget.title = this.getTranslation('click_to_stop')
    }

    // Update target input placeholder
    const targetElement = document.getElementById(this.targetIdValue)
    if (targetElement) {
      this.originalPlaceholder = targetElement.placeholder
      targetElement.placeholder = this.getTranslation('recording')
      targetElement.disabled = true
    }

    // Disable submit button while recording
    this.setSubmitButtonDisabled(true)
  }

  showTranscribingState() {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.getTranslation('transcribing')
    }

    const targetElement = document.getElementById(this.targetIdValue)
    if (targetElement) {
      targetElement.placeholder = this.getTranslation('transcribing')
    }
  }

  showIdleState() {
    if (this.hasMicIconTarget) {
      this.micIconTarget.classList.remove('hidden')
    }
    if (this.hasRecordingIconTarget) {
      this.recordingIconTarget.classList.add('hidden')
    }
    if (this.hasStatusTarget) {
      this.statusTarget.classList.add('hidden')
    }
    if (this.hasButtonTarget) {
      this.buttonTarget.classList.remove('text-red-600')
      this.buttonTarget.title = this.getTranslation('click_to_speak')
    }

    // Restore target input
    const targetElement = document.getElementById(this.targetIdValue)
    if (targetElement) {
      targetElement.placeholder = this.originalPlaceholder || this.getTranslation('default_placeholder')
      targetElement.disabled = false
      targetElement.focus()
    }

    // Re-enable submit button
    this.setSubmitButtonDisabled(false)
  }

  showError(message) {
    // You could integrate with a toast notification system here
    alert(message)
  }

  // Helper Methods

  getBestMimeType() {
    const possibleTypes = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
      'audio/mpeg'
    ]

    for (const type of possibleTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type
      }
    }

    return null
  }

  handleMicrophoneError(error) {
    let errorMessage = 'Unable to access microphone. '

    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      errorMessage += 'Please allow microphone access and try again.'
    } else if (error.name === 'NotFoundError') {
      errorMessage += 'No microphone found. Please connect a microphone and try again.'
    } else {
      errorMessage += 'Please check your browser settings and try again.'
    }

    this.showError(errorMessage)
  }

  releaseStream() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }
  }

  cleanup() {
    this.releaseStream()

    if (this.recordingTimeout) {
      clearTimeout(this.recordingTimeout)
      this.recordingTimeout = null
    }

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop()
    }
  }

  getCSRFToken() {
    const metaTag = document.querySelector('meta[name="csrf-token"]')
    return metaTag ? metaTag.getAttribute('content') : ''
  }

  setSubmitButtonDisabled(disabled) {
    // First try explicit submitButtonId value
    if (this.hasSubmitButtonIdValue && this.submitButtonIdValue) {
      const submitButton = document.getElementById(this.submitButtonIdValue)
      if (submitButton) {
        submitButton.disabled = disabled
        if (disabled) {
          submitButton.classList.add('opacity-50', 'cursor-not-allowed')
        } else {
          submitButton.classList.remove('opacity-50', 'cursor-not-allowed')
        }
        return
      }
    }

    // Fallback: find submit button in the same form as the target input
    const targetElement = document.getElementById(this.targetIdValue)
    if (targetElement) {
      const form = targetElement.closest('form')
      if (form) {
        const submitButton = form.querySelector('button[type="submit"], input[type="submit"]')
        if (submitButton) {
          submitButton.disabled = disabled
          if (disabled) {
            submitButton.classList.add('opacity-50', 'cursor-not-allowed')
          } else {
            submitButton.classList.remove('opacity-50', 'cursor-not-allowed')
          }
        }
      }
    }
  }

  getTranslation(key) {
    // Simple translation lookup - these can be overridden via data attributes
    const translations = {
      recording: 'Recording...',
      transcribing: 'Transcribing...',
      click_to_speak: 'Click to speak',
      click_to_stop: 'Click to stop recording',
      default_placeholder: 'Type your message here...'
    }

    // Check for data attribute override first
    const dataKey = `translation-${key.replace(/_/g, '-')}`
    const override = this.element.dataset[dataKey]

    return override || translations[key] || key
  }
}
